
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Trash2, Users, Plus, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

interface TestUser {
  id: string;
  user_id: string;
  username: string;
  created_at: string;
  batch_number: number;
}

const TestUserManager = () => {
  const [testUsers, setTestUsers] = useState<TestUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  const fetchTestUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('test_users')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTestUsers(data || []);
    } catch (error) {
      console.error('Error fetching test users:', error);
      toast.error('Failed to fetch test users');
    }
  };

  useEffect(() => {
    fetchTestUsers();
  }, []);

  const generateTestUsers = async () => {
    setGenerating(true);
    try {
      const nextBatchNumber = Math.max(...testUsers.map(u => u.batch_number), 0) + 1;
      
      const { data, error } = await supabase.functions.invoke('generate-test-users', {
        body: { count: 10, batchNumber: nextBatchNumber }
      });

      if (error) throw error;

      toast.success(`Successfully created ${data.created} test users in batch ${data.batch}`);
      await fetchTestUsers();
    } catch (error) {
      console.error('Error generating test users:', error);
      toast.error('Failed to generate test users');
    } finally {
      setGenerating(false);
    }
  };

  const deleteTestUser = async (testUserId: string, username: string) => {
    setDeleting(testUserId);
    try {
      const { error } = await supabase.rpc('delete_test_user', {
        test_user_id: testUserId
      });

      if (error) throw error;

      toast.success(`Deleted test user: ${username}`);
      await fetchTestUsers();
    } catch (error) {
      console.error('Error deleting test user:', error);
      toast.error(`Failed to delete test user: ${username}`);
    } finally {
      setDeleting(null);
    }
  };

  const deleteBatch = async (batchNumber: number) => {
    setLoading(true);
    try {
      const { data: deletedCount, error } = await supabase.rpc('delete_test_user_batch', {
        batch_num: batchNumber
      });

      if (error) throw error;

      toast.success(`Deleted ${deletedCount} test users from batch ${batchNumber}`);
      await fetchTestUsers();
    } catch (error) {
      console.error('Error deleting batch:', error);
      toast.error(`Failed to delete batch ${batchNumber}`);
    } finally {
      setLoading(false);
    }
  };

  const batches = Array.from(new Set(testUsers.map(u => u.batch_number))).sort((a, b) => b - a);

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="w-5 h-5" />
          Test User Manager
        </CardTitle>
        <CardDescription>
          Generate and manage test users for Alpha app testing
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Generate Button */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Generate New Test Users</h3>
            <p className="text-sm text-muted-foreground">
              Creates 10 test users with usernames X001-X010, ages 20-60, 3+ photos each
            </p>
          </div>
          <Button 
            onClick={generateTestUsers} 
            disabled={generating}
            className="min-w-[120px]"
          >
            <Plus className="w-4 h-4 mr-2" />
            {generating ? 'Generating...' : 'Generate 10'}
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-muted/50 p-4 rounded-lg">
            <div className="text-2xl font-bold">{testUsers.length}</div>
            <div className="text-sm text-muted-foreground">Total Test Users</div>
          </div>
          <div className="bg-muted/50 p-4 rounded-lg">
            <div className="text-2xl font-bold">{batches.length}</div>
            <div className="text-sm text-muted-foreground">Batches Created</div>
          </div>
        </div>

        {/* Batch Management */}
        {batches.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold">Manage Batches</h3>
            {batches.map(batchNumber => {
              const batchUsers = testUsers.filter(u => u.batch_number === batchNumber);
              return (
                <div key={batchNumber} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <span className="font-medium">Batch {batchNumber}</span>
                    <span className="text-sm text-muted-foreground ml-2">
                      ({batchUsers.length} users)
                    </span>
                    <div className="text-xs text-muted-foreground mt-1">
                      Created: {new Date(batchUsers[0]?.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" size="sm" disabled={loading}>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Batch
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Batch {batchNumber}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will permanently delete all {batchUsers.length} test users in batch {batchNumber}. 
                          This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={() => deleteBatch(batchNumber)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Delete Batch
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              );
            })}
          </div>
        )}

        {/* Individual User Management */}
        {testUsers.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold">Individual Test Users</h3>
            <div className="grid gap-3 max-h-96 overflow-y-auto">
              {testUsers.map(user => (
                <div key={user.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div>
                      <span className="font-medium">{user.username}</span>
                      <div className="text-xs text-muted-foreground">
                        Created: {new Date(user.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <Badge variant="secondary">Batch {user.batch_number}</Badge>
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button 
                        variant="destructive" 
                        size="sm" 
                        disabled={deleting === user.id}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete {user.username}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will permanently delete the test user {user.username} and all associated data. 
                          This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={() => deleteTestUser(user.id, user.username)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Delete User
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ))}
            </div>
          </div>
        )}

        {testUsers.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No test users created yet.</p>
            <p className="text-sm">Click "Generate 10" to create your first batch.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TestUserManager;
